import { createSlice } from "@reduxjs/toolkit";
import {
  fetchRevenueReport,
  fetchCollectionsReport,
  fetchClinicianHoursReport,
  fetchClientDropoffReport,
  fetchActiveClientsReport,
  fetchClientHistoryReport,
  fetchRecallClientsReport,
  fetchClinicianAvailabilityReport,
  fetchClinicianScheduleReport,
  fetchQuickBooksInvoicesReport,
  fetchQuickBooksPaymentsReport,
  fetchQuickBooksAuditReport,
  fetchClinicPerformanceReport,
  fetchBlockTherapyReport,
  fetchClientJourneyProgressReport,
  fetchPayrollSummaryReport,
  fetchPayrollAuditReport,
  fetchCancellationTrackingReport,
} from "../thunks/reportsThunk";

const initialState = {
  revenue: { data: null, loading: false, error: null },
  collections: { data: null, loading: false, error: null },
  clinicianHours: { data: null, loading: false, error: null },
  clinicianAvailability: { data: null, loading: false, error: null },
  clinicianSchedule: { data: null, loading: false, error: null },
  quickBooksInvoices: { data: null, loading: false, error: null },
  quickBooksPayments: { data: null, loading: false, error: null },
  quickBooksAudit: { data: null, loading: false, error: null },
  clinicPerformance: { data: null, loading: false, error: null },
  blockTherapy: { data: null, loading: false, error: null },
  clientDropoff: { data: null, loading: false, error: null },
  activeClients: { data: null, loading: false, error: null },
  clientHistory: { data: [], loading: false, error: null },
  recallClients: { data: [], loading: false, error: null },
  clientJourneyProgress: { data: null, loading: false, error: null },
  payrollSummary: { data: null, loading: false, error: null },
  payrollAudit: { data: null, loading: false, error: null },
  cancellationTracking: { data: null, loading: false, error: null },
};

const reportsSlice = createSlice({
  name: "reports",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Revenue Report
    builder
      .addCase(fetchRevenueReport.pending, (state) => {
        if (!state.revenue) {
          state.revenue = { data: null, loading: false, error: null };
        }
        state.revenue.loading = true;
        state.revenue.error = null;
      })
      .addCase(fetchRevenueReport.fulfilled, (state, action) => {
        if (!state.revenue) {
          state.revenue = { data: null, loading: false, error: null };
        }
        state.revenue.loading = false;
        state.revenue.data = action.payload;
      })
      .addCase(fetchRevenueReport.rejected, (state, action) => {
        if (!state.revenue) {
          state.revenue = { data: null, loading: false, error: null };
        }
        state.revenue.loading = false;
        state.revenue.error = action.payload;
      });

    // Collections Report
    builder
      .addCase(fetchCollectionsReport.pending, (state) => {
        if (!state.collections) {
          state.collections = { data: null, loading: false, error: null };
        }
        state.collections.loading = true;
        state.collections.error = null;
      })
      .addCase(fetchCollectionsReport.fulfilled, (state, action) => {
        if (!state.collections) {
          state.collections = { data: null, loading: false, error: null };
        }
        state.collections.loading = false;
        state.collections.data = action.payload;
      })
      .addCase(fetchCollectionsReport.rejected, (state, action) => {
        if (!state.collections) {
          state.collections = { data: null, loading: false, error: null };
        }
        state.collections.loading = false;
        state.collections.error = action.payload;
      });

    // Clinician Hours Report
    builder
      .addCase(fetchClinicianHoursReport.pending, (state) => {
        if (!state.clinicianHours) {
          state.clinicianHours = { data: null, loading: false, error: null };
        }
        state.clinicianHours.loading = true;
        state.clinicianHours.error = null;
      })
      .addCase(fetchClinicianHoursReport.fulfilled, (state, action) => {
        if (!state.clinicianHours) {
          state.clinicianHours = { data: null, loading: false, error: null };
        }
        state.clinicianHours.loading = false;
        state.clinicianHours.data = action.payload;
      })
      .addCase(fetchClinicianHoursReport.rejected, (state, action) => {
        if (!state.clinicianHours) {
          state.clinicianHours = { data: null, loading: false, error: null };
        }
        state.clinicianHours.loading = false;
        state.clinicianHours.error = action.payload;
      });

    // Client Dropoff Report
    builder
      .addCase(fetchClientDropoffReport.pending, (state) => {
        if (!state.clientDropoff) {
          state.clientDropoff = { data: null, loading: false, error: null };
        }
        state.clientDropoff.loading = true;
        state.clientDropoff.error = null;
      })
      .addCase(fetchClientDropoffReport.fulfilled, (state, action) => {
        if (!state.clientDropoff) {
          state.clientDropoff = { data: null, loading: false, error: null };
        }
        state.clientDropoff.loading = false;
        state.clientDropoff.data = action.payload;
      })
      .addCase(fetchClientDropoffReport.rejected, (state, action) => {
        if (!state.clientDropoff) {
          state.clientDropoff = { data: null, loading: false, error: null };
        }
        state.clientDropoff.loading = false;
        state.clientDropoff.error = action.payload;
      });

    // Active Clients Report
    builder
      .addCase(fetchActiveClientsReport.pending, (state) => {
        if (!state.activeClients) {
          state.activeClients = { data: null, loading: false, error: null };
        }
        state.activeClients.loading = true;
        state.activeClients.error = null;
      })
      .addCase(fetchActiveClientsReport.fulfilled, (state, action) => {
        if (!state.activeClients) {
          state.activeClients = { data: null, loading: false, error: null };
        }
        state.activeClients.loading = false;
        state.activeClients.data = action.payload;
      })
      .addCase(fetchActiveClientsReport.rejected, (state, action) => {
        if (!state.activeClients) {
          state.activeClients = { data: null, loading: false, error: null };
        }
        state.activeClients.loading = false;
        state.activeClients.error = action.payload;
      });

    // Client History Report
    builder
      .addCase(fetchClientHistoryReport.pending, (state) => {
        if (!state.clientHistory) {
          state.clientHistory = { data: [], loading: false, error: null };
        }
        state.clientHistory.loading = true;
        state.clientHistory.error = null;
      })
      .addCase(fetchClientHistoryReport.fulfilled, (state, action) => {
        if (!state.clientHistory) {
          state.clientHistory = { data: [], loading: false, error: null };
        }
        state.clientHistory.loading = false;
        state.clientHistory.data = action.payload;
      })
      .addCase(fetchClientHistoryReport.rejected, (state, action) => {
        if (!state.clientHistory) {
          state.clientHistory = { data: [], loading: false, error: null };
        }
        state.clientHistory.loading = false;
        state.clientHistory.error = action.payload;
      });

    // Recall Clients Report
    builder
      .addCase(fetchRecallClientsReport.pending, (state) => {
        if (!state.recallClients) {
          state.recallClients = { data: [], loading: false, error: null };
        }
        state.recallClients.loading = true;
        state.recallClients.error = null;
      })
      .addCase(fetchRecallClientsReport.fulfilled, (state, action) => {
        if (!state.recallClients) {
          state.recallClients = { data: [], loading: false, error: null };
        }
        state.recallClients.loading = false;
        state.recallClients.data = action.payload;
      })
      .addCase(fetchRecallClientsReport.rejected, (state, action) => {
        if (!state.recallClients) {
          state.recallClients = { data: [], loading: false, error: null };
        }
        state.recallClients.loading = false;
        state.recallClients.error = action.payload;
      });

    // Clinician Availability Report
    builder
      .addCase(fetchClinicianAvailabilityReport.pending, (state) => {
        if (!state.clinicianAvailability) {
          state.clinicianAvailability = { data: null, loading: false, error: null };
        }
        state.clinicianAvailability.loading = true;
        state.clinicianAvailability.error = null;
      })
      .addCase(fetchClinicianAvailabilityReport.fulfilled, (state, action) => {
        if (!state.clinicianAvailability) {
          state.clinicianAvailability = { data: null, loading: false, error: null };
        }
        state.clinicianAvailability.loading = false;
        state.clinicianAvailability.data = action.payload;
      })
      .addCase(fetchClinicianAvailabilityReport.rejected, (state, action) => {
        if (!state.clinicianAvailability) {
          state.clinicianAvailability = { data: null, loading: false, error: null };
        }
        state.clinicianAvailability.loading = false;
        state.clinicianAvailability.error = action.payload;
      });

    // Clinician Schedule Report
    builder
      .addCase(fetchClinicianScheduleReport.pending, (state) => {
        if (!state.clinicianSchedule) {
          state.clinicianSchedule = { data: null, loading: false, error: null };
        }
        state.clinicianSchedule.loading = true;
        state.clinicianSchedule.error = null;
      })
      .addCase(fetchClinicianScheduleReport.fulfilled, (state, action) => {
        if (!state.clinicianSchedule) {
          state.clinicianSchedule = { data: null, loading: false, error: null };
        }
        state.clinicianSchedule.loading = false;
        state.clinicianSchedule.data = action.payload;
      })
      .addCase(fetchClinicianScheduleReport.rejected, (state, action) => {
        if (!state.clinicianSchedule) {
          state.clinicianSchedule = { data: null, loading: false, error: null };
        }
        state.clinicianSchedule.loading = false;
        state.clinicianSchedule.error = action.payload;
      });

    // QuickBooks Invoices Report
    builder
      .addCase(fetchQuickBooksInvoicesReport.pending, (state) => {
        if (!state.quickBooksInvoices) {
          state.quickBooksInvoices = { data: null, loading: false, error: null };
        }
        state.quickBooksInvoices.loading = true;
        state.quickBooksInvoices.error = null;
      })
      .addCase(fetchQuickBooksInvoicesReport.fulfilled, (state, action) => {
        if (!state.quickBooksInvoices) {
          state.quickBooksInvoices = { data: null, loading: false, error: null };
        }
        state.quickBooksInvoices.loading = false;
        state.quickBooksInvoices.data = action.payload;
      })
      .addCase(fetchQuickBooksInvoicesReport.rejected, (state, action) => {
        if (!state.quickBooksInvoices) {
          state.quickBooksInvoices = { data: null, loading: false, error: null };
        }
        state.quickBooksInvoices.loading = false;
        state.quickBooksInvoices.error = action.payload;
      });

    // QuickBooks Payments Report
    builder
      .addCase(fetchQuickBooksPaymentsReport.pending, (state) => {
        if (!state.quickBooksPayments) {
          state.quickBooksPayments = { data: null, loading: false, error: null };
        }
        state.quickBooksPayments.loading = true;
        state.quickBooksPayments.error = null;
      })
      .addCase(fetchQuickBooksPaymentsReport.fulfilled, (state, action) => {
        if (!state.quickBooksPayments) {
          state.quickBooksPayments = { data: null, loading: false, error: null };
        }
        state.quickBooksPayments.loading = false;
        state.quickBooksPayments.data = action.payload;
      })
      .addCase(fetchQuickBooksPaymentsReport.rejected, (state, action) => {
        if (!state.quickBooksPayments) {
          state.quickBooksPayments = { data: null, loading: false, error: null };
        }
        state.quickBooksPayments.loading = false;
        state.quickBooksPayments.error = action.payload;
      });

    // QuickBooks Audit Report
    builder
      .addCase(fetchQuickBooksAuditReport.pending, (state) => {
        if (!state.quickBooksAudit) {
          state.quickBooksAudit = { data: null, loading: false, error: null };
        }
        state.quickBooksAudit.loading = true;
        state.quickBooksAudit.error = null;
      })
      .addCase(fetchQuickBooksAuditReport.fulfilled, (state, action) => {
        if (!state.quickBooksAudit) {
          state.quickBooksAudit = { data: null, loading: false, error: null };
        }
        state.quickBooksAudit.loading = false;
        state.quickBooksAudit.data = action.payload;
      })
      .addCase(fetchQuickBooksAuditReport.rejected, (state, action) => {
        if (!state.quickBooksAudit) {
          state.quickBooksAudit = { data: null, loading: false, error: null };
        }
        state.quickBooksAudit.loading = false;
        state.quickBooksAudit.error = action.payload;
      });

    // Clinic Performance Report
    builder
      .addCase(fetchClinicPerformanceReport.pending, (state) => {
        if (!state.clinicPerformance) {
          state.clinicPerformance = { data: null, loading: false, error: null };
        }
        state.clinicPerformance.loading = true;
        state.clinicPerformance.error = null;
      })
      .addCase(fetchClinicPerformanceReport.fulfilled, (state, action) => {
        if (!state.clinicPerformance) {
          state.clinicPerformance = { data: null, loading: false, error: null };
        }
        state.clinicPerformance.loading = false;
        state.clinicPerformance.data = action.payload;
      })
      .addCase(fetchClinicPerformanceReport.rejected, (state, action) => {
        if (!state.clinicPerformance) {
          state.clinicPerformance = { data: null, loading: false, error: null };
        }
        state.clinicPerformance.loading = false;
        state.clinicPerformance.error = action.payload;
      });

    // Block Therapy Report
    builder
      .addCase(fetchBlockTherapyReport.pending, (state) => {
        if (!state.blockTherapy) {
          state.blockTherapy = { data: null, loading: false, error: null };
        }
        state.blockTherapy.loading = true;
        state.blockTherapy.error = null;
      })
      .addCase(fetchBlockTherapyReport.fulfilled, (state, action) => {
        if (!state.blockTherapy) {
          state.blockTherapy = { data: null, loading: false, error: null };
        }
        state.blockTherapy.loading = false;
        state.blockTherapy.data = action.payload;
      })
      .addCase(fetchBlockTherapyReport.rejected, (state, action) => {
        if (!state.blockTherapy) {
          state.blockTherapy = { data: null, loading: false, error: null };
        }
        state.blockTherapy.loading = false;
        state.blockTherapy.error = action.payload;
      });

    // Client Journey Progress Report
    builder
      .addCase(fetchClientJourneyProgressReport.pending, (state) => {
        state.clientJourneyProgress.loading = true;
        state.clientJourneyProgress.error = null;
      })
      .addCase(fetchClientJourneyProgressReport.fulfilled, (state, action) => {
        state.clientJourneyProgress.loading = false;
        state.clientJourneyProgress.data = action.payload;
      })
      .addCase(fetchClientJourneyProgressReport.rejected, (state, action) => {
        state.clientJourneyProgress.loading = false;
        state.clientJourneyProgress.error = action.payload;
      });

    // Payroll Summary Report
    builder
      .addCase(fetchPayrollSummaryReport.pending, (state) => {
        if (!state.payrollSummary) {
          state.payrollSummary = { data: null, loading: false, error: null };
        }
        state.payrollSummary.loading = true;
        state.payrollSummary.error = null;
      })
      .addCase(fetchPayrollSummaryReport.fulfilled, (state, action) => {
        if (!state.payrollSummary) {
          state.payrollSummary = { data: null, loading: false, error: null };
        }
        state.payrollSummary.loading = false;
        state.payrollSummary.data = action.payload.data;
      })
      .addCase(fetchPayrollSummaryReport.rejected, (state, action) => {
        if (!state.payrollSummary) {
          state.payrollSummary = { data: null, loading: false, error: null };
        }
        state.payrollSummary.loading = false;
        state.payrollSummary.error = action.payload;
      });

    // Payroll Audit Report
    builder
      .addCase(fetchPayrollAuditReport.pending, (state) => {
        if (!state.payrollAudit) {
          state.payrollAudit = { data: null, loading: false, error: null };
        }
        state.payrollAudit.loading = true;
        state.payrollAudit.error = null;
      })
      .addCase(fetchPayrollAuditReport.fulfilled, (state, action) => {
        if (!state.payrollAudit) {
          state.payrollAudit = { data: null, loading: false, error: null };
        }
        state.payrollAudit.loading = false;
        state.payrollAudit.data = action.payload.data;
      })
      .addCase(fetchPayrollAuditReport.rejected, (state, action) => {
        if (!state.payrollAudit) {
          state.payrollAudit = { data: null, loading: false, error: null };
        }
        state.payrollAudit.loading = false;
        state.payrollAudit.error = action.payload;
      });

    // Cancellation Tracking Report
    builder
      .addCase(fetchCancellationTrackingReport.pending, (state) => {
        if (!state.cancellationTracking) {
          state.cancellationTracking = { data: null, loading: false, error: null };
        }
        state.cancellationTracking.loading = true;
        state.cancellationTracking.error = null;
      })
      .addCase(fetchCancellationTrackingReport.fulfilled, (state, action) => {
        if (!state.cancellationTracking) {
          state.cancellationTracking = { data: null, loading: false, error: null };
        }
        state.cancellationTracking.loading = false;
        state.cancellationTracking.data = action.payload.data;
      })
      .addCase(fetchCancellationTrackingReport.rejected, (state, action) => {
        if (!state.cancellationTracking) {
          state.cancellationTracking = { data: null, loading: false, error: null };
        }
        state.cancellationTracking.loading = false;
        state.cancellationTracking.error = action.payload;
      });
  },
});

export default reportsSlice.reducer;
